import { FormattedMessage } from "react-intl";
import styles from "./SubscriptionPopover.module.scss";
import FloatingBoxWithArrow from "../../../../common/core-ui/FloatingBoxWithArrow.tsx";
import { ComponentPropsWithoutRef, useMemo } from "react";
import { useHomeContextUser } from "../../homeContext.ts";
import useFractionPersistValue, {
  FractionPersistKey,
} from "../../../../common/core-ui/useFractionPersistValue.ts";

type BoxProps = Omit<
  ComponentPropsWithoutRef<typeof FloatingBoxWithArrow>,
  "children" | "onClose"
>;

const getUserSubscriptionVbids = (
  user: ReturnType<typeof useHomeContextUser>
) => {
  const subscriptions: string[] = [];
  user.creditList.forEach((credit) => {
    if (credit.type === "S" && credit.vbid) {
      subscriptions.push(credit.vbid);
    } else if (credit.type === "E" && credit.subscriptionVbid) {
      subscriptions.push(credit.subscriptionVbid);
    } else if (credit.subscriptions) {
      subscriptions.push(...credit.subscriptions);
    }
  });
  return subscriptions;
};

export default function SubscriptionPopover({
  setFloating,
  arrowRef,
  context,
  floatingStyles,
  arrowStaticOffset,
}: BoxProps) {
  // From fraction: local storage instead of preferences, key/value pair name/structure
  // https://github.com/vitalsource/fraction/blob/521e459f1a0d16ed3a563af1ad736b32c86b9f77/src/js/fraction/ui/apps/library/subscription-popover/index.js#L62-L85
  const [storedValue, setStoredValue] = useFractionPersistValue<{
    viewedAt: string;
  }>(FractionPersistKey.SubscriptionPopoverStatus);
  const user = useHomeContextUser();

  const hasPromoSub = useMemo(() => {
    const triggeringSubs = [
      "SUB-7804702992", // dev
      "SUB-7449856969", //prod
    ];

    return !!getUserSubscriptionVbids(user).find((userSub) => {
      return triggeringSubs.includes(userSub);
    });
  }, [user]);

  if (!hasPromoSub || storedValue) {
    return null;
  }

  return (
    <FloatingBoxWithArrow
      className={styles.floatingBox}
      role="alert"
      setFloating={setFloating}
      arrowRef={arrowRef}
      context={context}
      floatingStyles={floatingStyles}
      arrowStaticOffset={arrowStaticOffset}
      onClose={() => {
        setStoredValue({ viewedAt: new Date().toISOString() });
        if (context?.elements.reference instanceof HTMLElement) {
          context.elements.reference.focus();
        }
      }}
    >
      <div className={styles.title}>
        <FormattedMessage defaultMessage="Subscription added" />
      </div>
      <FormattedMessage
        tagName="p"
        defaultMessage="New subscriptions will show up in your Explore tab. When you open a book, it will appear in My Library."
      />
    </FloatingBoxWithArrow>
  );
}
